/**
 * environment.ts
 *
 * Environment detection and configuration utilities
 *
 * IMPORTANT: Staging is a production-parity environment!
 * - Same Sentry sampling (10%), caching, monitoring as production
 * - Only difference: test data and staging URLs
 * - Use isStaging() to identify the environment (for logging/tracking only)
 * - DO NOT disable features in staging (it must replicate production exactly)
 */

/**
 * Environment type enum
 */
export enum Environment {
  Development = 'development',
  Staging = 'staging',
  Production = 'production',
}

/**
 * Get the current environment
 * Determined by VITE_SENTRY_ENVIRONMENT or VITE_APP_ENV env var
 */
export function getEnvironment(): Environment {
  const sentryEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  const appEnv = import.meta.env.VITE_APP_ENV;
  const env = sentryEnv || appEnv || 'development';
  
  return (env as Environment) || Environment.Development;
}

/**
 * Check if running in development
 */
export function isDevelopment(): boolean {
  return getEnvironment() === Environment.Development;
}

/**
 * Check if running in staging
 * 
 * IMPORTANT: Staging mirrors production exactly!
 * Use isStaging() for analytics/logging only, NOT to disable features.
 * All monitoring, caching, and performance configurations are production-like.
 */
export function isStaging(): boolean {
  return getEnvironment() === Environment.Staging;
}

/**
 * Check if running in production
 */
export function isProduction(): boolean {
  return getEnvironment() === Environment.Production;
}

/**
 * Check if in staging or production (not development)
 */
export function isDeployedEnvironment(): boolean {
  return isStaging() || isProduction();
}

/**
 * Check if debugging should be enabled
 * Debug mode is enabled in development ONLY
 * Staging mirrors production, so no debug mode
 */
export function isDebugEnabled(): boolean {
  return isDevelopment();
}

/**
 * Get the base URL for the current environment
 */
export function getEnvironmentBaseUrl(): string {
  const protocol = window.location.protocol;
  const host = window.location.host;
  return `${protocol}//${host}`;
}

/**
 * Get environment-specific config for monitoring services
 * 
 * PRODUCTION-PARITY: Staging uses same configuration as production
 */
export const EnvironmentConfig = {
  /**
   * Sentry sampling rates
   * Development: Debug (100%)
   * Staging/Production: 10% (cost optimization, same as production)
   */
  sentryTracesSampleRate(): number {
    return isDevelopment() ? 1.0 : 0.1;
  },

  sentryReplaySessionSampleRate(): number {
    return isDevelopment() ? 1.0 : 0.1;
  },

  /**
   * Enable Sentry error reporting
   * Disabled in development only
   */
  sentryEnabled(): boolean {
    return isDeployedEnvironment();
  },

  /**
   * Debug logging configuration
   * Enabled in development ONLY
   */
  debugLoggingEnabled(): boolean {
    return isDevelopment();
  },

  /**
   * Mixpanel debug mode
   * Development only
   */
  mixpanelDebugMode(): boolean {
    return isDevelopment();
  },

  /**
   * Console logging level for debugging
   * Development: debug, Staging/Production: error (minimal)
   */
  logLevel(): 'debug' | 'info' | 'warn' | 'error' {
    if (isDevelopment()) return 'debug';
    return 'error';
  },

  /**
   * Enable performance monitoring
   * Enabled in staging/production
   */
  enablePerformanceMonitoring(): boolean {
    return isDeployedEnvironment();
  },

  /**
   * Enable detailed API request logging
   * Development only
   */
  enableAPIRequestLogging(): boolean {
    return isDevelopment();
  },

  /**
   * Enable React DevTools
   * Development only
   */
  enableReactDevTools(): boolean {
    return isDevelopment();
  },

  /**
   * Timeout values for all environments
   * Same for staging and production (production-parity)
   */
  getRequestTimeout(): number {
    if (isDevelopment()) return 30000;  // 30s for local debugging
    return 20000;                        // 20s for staging/production
  },

  /**
   * Retry configuration
   * Same for staging and production
   */
  getMaxRetries(): number {
    if (isDevelopment()) return 2;
    return 3;                            // 3 retries for staging/production
  },

  /**
   * Enable feature flags testing in development only
   * Staging mirrors production (no feature flag testing UI)
   */
  enableFeatureFlagTesting(): boolean {
    return isDevelopment();
  },

  /**
   * Show development-only UI components
   * Development only
   */
  showDevTools(): boolean {
    return isDevelopment();
  },

  /**
   * Show staging banner and warnings
   * Staging and production: NO banners
   * Staging mirrors production exactly
   */
  showStagingBanner(): boolean {
    return false; // Never show - staging is prod-parity
  },
};

/**
 * Log an environment info message
 * Called at app startup to confirm which environment is running
 */
export function logEnvironmentInfo(): void {
  const env = getEnvironment();
  const isDev = isDevelopment();
  const apiUrl = import.meta.env.VITE_API_URL;
  
  console.log(
    `%c⚙️  ScholarMinds running in ${env.toUpperCase()} mode`,
    isDev ? 'color: #4CAF50; font-weight: bold; font-size: 14px;' : 'color: #2196F3; font-weight: bold; font-size: 14px;'
  );
  
  console.log(`API URL: ${apiUrl}`);
  
  if (isStaging()) {
    console.log('%c🔬 STAGING ENVIRONMENT - Test data environment (production-parity)', 'color: #2196F3; font-weight: bold;');
  }
  
  if (isProduction()) {
    console.log('%c🔒 Production environment', 'color: #F44336; font-weight: bold;');
  }
}

/**
 * Environment details for debugging
 */
export interface EnvironmentInfo {
  environment: Environment;
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
  apiUrl: string;
  appVersion: string;
  sentryEnvironment: string;
  timestamp: string;
}

/**
 * Get complete environment information
 * Useful for diagnostics and debugging
 */
export function getEnvironmentInfo(): EnvironmentInfo {
  return {
    environment: getEnvironment(),
    isDevelopment: isDevelopment(),
    isStaging: isStaging(),
    isProduction: isProduction(),
    apiUrl: import.meta.env.VITE_API_URL,
    appVersion: import.meta.env.VITE_SENTRY_RELEASE || 'unknown',
    sentryEnvironment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'unknown',
    timestamp: new Date().toISOString(),
  };
}

